// ─────────────────────────────────────────────────────────────────────────
// Long-form text pages (Shipping & Returns, Privacy, Terms).
// Structure only — copy must be signed off by the client before launch.
// ─────────────────────────────────────────────────────────────────────────

export type TextBlock =
  | { type: 'p'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'note'; text: string }
  | { type: 'table'; head: string[]; rows: string[][] }

export type TextSection = {
  id: string
  title: string
  blocks: TextBlock[]
}

export const shippingReturnsSections: TextSection[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    blocks: [
      {
        type: 'p',
        text: 'Ecoffee Cup® South Africa does not allow returns once a product has been purchased, unless there is clear evidence that the product has a factory defect. Products purchased on the Ecoffee Cup® South Africa website are subject to a 7-day return and cooling-off policy, subject to the terms and conditions set out below.',
      },
      {
        type: 'p',
        text: 'This right of return only applies to products returned in their original, complete and unused condition. Any product that has been damaged, is not in its original packaging, or has packaging that has been worn beyond simply opening the product, will not be refunded.',
      },
      {
        type: 'note',
        text: 'For all enquiries relating to orders placed through this website only, email sales@ecoffeecup.co.za and quote your order number in the subject line of all correspondence.',
      },
    ],
  },
  {
    id: 'shipping',
    title: 'Shipping',
    blocks: [
      {
        type: 'p',
        text: 'We ship nationwide with a tracked courier service. Orders are packed within one working day and you will receive a tracking number by email as soon as your parcel leaves us.',
      },
      {
        type: 'table',
        head: ['Destination', 'Delivery time', 'Cost'],
        rows: [
          ['Main centres', '2–3 working days', 'R 75'],
          ['Regional areas', '3–5 working days', 'R 110'],
          ['Outlying areas', '5–7 working days', 'R 150'],
          ['Orders over R 750', 'As above', 'Free'],
        ],
      },
      { type: 'h3', text: 'Delivery addresses' },
      {
        type: 'p',
        text: 'We deliver to street addresses only, between 08h00 and 17h00 on working days. We cannot deliver to a PO Box. Please give an address where somebody can sign for the parcel — a workplace address is often easier than a home address.',
      },
      { type: 'h3', text: 'Failed deliveries' },
      {
        type: 'p',
        text: 'Our courier will make two delivery attempts. If nobody is available on the second attempt the parcel returns to us, and a redelivery fee will apply before it can be sent out again.',
      },
    ],
  },
  {
    id: 'returns',
    title: 'Returns',
    blocks: [
      {
        type: 'p',
        text: 'At Ecoffee Cup® South Africa we are committed to providing high-quality products and excellent customer service. This policy explains your rights and our procedures regarding returns, exchanges, repairs and refunds.',
      },
      { type: 'h3', text: 'Consumer rights' },
      {
        type: 'p',
        text: 'These return and replacement terms and conditions are subject to the Consumer Protection Act, 68 of 2008 (“CPA”), and any other applicable South African consumer protection laws. Nothing in this policy limits your rights under that Act.',
      },
      { type: 'h3', text: 'Defective, damaged or faulty products' },
      {
        type: 'p',
        text: 'If your product is defective, damaged on delivery, or develops a fault that is covered under the CPA, you may be entitled to a repair, replacement or refund. Please contact us within seven days of receiving your order, with photographs of the fault and your order number.',
      },
      { type: 'h3', text: 'What we cannot accept' },
      {
        type: 'list',
        items: [
          'Products that have been used, washed or filled.',
          'Products returned without their original, unworn packaging.',
          'Damage caused by dropping, microwaving, dishwashing at high temperature, or general wear.',
          'Custom-branded and corporate orders, which are produced to your specification and are non-returnable.',
          'Sale items, unless the product is defective.',
        ],
      },
    ],
  },
  {
    id: 'how-to-return',
    title: 'How to return an item',
    blocks: [
      {
        type: 'list',
        items: [
          'Email sales@ecoffeecup.co.za within seven days of delivery, with your order number in the subject line.',
          'Include photographs of the product and its packaging, and a short description of the fault.',
          'We will confirm by email whether the return is approved and give you the return address.',
          'Send the product back in its original packaging, using a tracked service.',
          'Once we have received and inspected it, we will confirm the outcome within five working days.',
        ],
      },
      { type: 'h3', text: 'Return shipping costs' },
      {
        type: 'p',
        text: 'Where a product is confirmed defective, we cover the cost of returning it. Where a return is due to a change of mind within the cooling-off period, the return shipping cost is yours.',
      },
    ],
  },
  {
    id: 'refunds',
    title: 'Refunds & exchanges',
    blocks: [
      {
        type: 'p',
        text: 'Approved refunds are processed to the original payment method within ten working days of the return being inspected. Depending on your bank, it may take a few days more to reflect.',
      },
      {
        type: 'p',
        text: 'If you would prefer an exchange, tell us in your first email. Where the replacement is a different price, we will refund or invoice the difference before shipping.',
      },
      { type: 'h3', text: 'Cancelled orders' },
      {
        type: 'p',
        text: 'An order can be cancelled at no cost any time before it is handed to the courier. Once it has shipped, the returns process above applies.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact us',
    blocks: [
      {
        type: 'p',
        text: 'If anything here is unclear, or you would like to check whether your situation qualifies before sending a parcel back, get in touch and we will help.',
      },
      {
        type: 'list',
        items: [
          'Email — sales@ecoffeecup.co.za',
          'Phone — 021 000 0000, weekdays 08h00–16h30',
          'Post — returns address supplied on approval of your return',
        ],
      },
    ],
  },
]
